'use client';

/**
 * The one navbar of the whole site (owner, 2026-10-14: "the navbar should be
 * working as something global"). Translate, Camps and Games all wear it, so
 * moving between them never looks like changing product.
 *
 *   ┌ [lead] (coin) Kharma Translate · Translate Camps Games │   [ search ]   │ tools · KHARMA · bell · (you) [✕] ┐
 *       ───────────── left ─────────────────────────────   ── centre ──    ───────────── right ─────────────
 *
 * Three zones (owner, 2026-10-15: "the logo, branding name and items on the
 * left, the search box in the centre, the rest on the right"):
 *   left    a page's own menu button (the Camps ☰ when its menu is a drawer),
 *           the Kharma coin and wordmark (the way home), and the sections as
 *           tabs, the lit one being where you are
 *   centre  the search box, on every page: the page's own (`search`: Camps
 *           search, the Games shelf's filter) or, by default, a search of all
 *           of Kharma (SiteSearch: Camps and games). Centred on the bar.
 *   right   the page's tools (`children`: a running game's name),
 *           KHARMA (with the daily reward behind it), notifications, the
 *           avatar menu (AccountMenu), and ✕ only on a layer over its page
 *           (`closeButton` replaces it: the game's "Close game")
 *
 * While a game runs, `gamesTab` decorates the Games tab (tooltip, and a click
 * that goes to the Games home), folded into the switcher too.
 *
 * It follows the app's light or dark theme like the rest of the page (or is
 * dark over an always-dark page, `tone`). It is glass — the page shows through,
 * blurred — flat at the top of the page and edged with a hairline and a soft
 * shadow once the page scrolls under it, and it steps out of the way while
 * you scroll down and returns when you scroll up (useHeadroom).
 *
 * What gives way as the row narrows, measured (lib/useFit), first to go first:
 * the KHARMA chip's number, the wordmark (the coin stays), the section labels
 * (their icons stay), and then the tabs fold into one switcher showing the
 * section you are in.
 */

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useAuth } from '@/lib/auth';
import { useFit } from '@/lib/useFit';
import { BRAND } from '@/lib/brand';
import BrandMark from '../BrandMark';
import DailyReward from '../DailyReward';
import NotificationBell from '../notifications/NotificationBell';
import ChatButton from '../chat/ChatButton';
import DevAgentHotkey from '../devagent/DevAgentHotkey';
import AccountMenu from './AccountMenu';
import CampSearch from '../camps/CampSearch';
import { TentIcon } from '../camps/bits';
import { NAV_BTN } from './styles';

export { NAV_BTN, NAV_SEARCH } from './styles';

export type Section = 'translate' | 'camps' | 'games' | 'world';

/** The section an address belongs to. */
export function sectionOf(path: string): Section | null {
  if (path === '/' || path.startsWith('/welcome')) return 'translate';
  if (/^\/(camps|c|u|play)(\/|$)/.test(path)) return 'camps';
  if (/^\/games(\/|$)/.test(path)) return 'games';
  if (/^\/world(\/|$)/.test(path)) return 'world';
  return null;
}

const ICON = 'h-[18px] w-[18px] shrink-0';
export function TranslateIcon({ className = ICON }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 5h9M8.5 3v2M6 5c.6 3 2.7 5.6 5.5 7M11 5c-.8 3.6-3.4 6.5-7 8" />
      <path d="M12.5 21l4-9 4 9M13.8 18h5.4" />
    </svg>
  );
}
/** The Camps tent, the same mark Camps uses everywhere. */
export function CampsIcon({ className = ICON }: { className?: string }) {
  return <TentIcon className={className} />;
}
export function GamesIcon({ className = ICON }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6.5 8h11a4.5 4.5 0 014.4 3.6l.7 4A3.4 3.4 0 0119.3 19c-1 0-2-.5-2.6-1.4L15.5 16h-7l-1.2 1.6A3.2 3.2 0 014.7 19a3.4 3.4 0 01-3.3-4l.7-4A4.5 4.5 0 016.5 8z" />
      <path d="M7 11v3M5.5 12.5h3" />
      <circle cx="16" cy="11.6" r=".9" fill="currentColor" stroke="none" />
      <circle cx="18" cy="13.6" r=".9" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** TrueMarch 2D: a compass rose. */
export function WorldIcon({ className = ICON }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 8.5l-2 5-5 2 2-5z" />
    </svg>
  );
}

const SECTIONS: { id: Section; label: string; href: string; icon: ReactNode; hint: string }[] = [
  { id: 'translate', label: 'Translate', href: '/', icon: <TranslateIcon />, hint: 'Translate, simplify, humanize and fix mistakes' },
  { id: 'camps', label: 'Camps', href: '/camps', icon: <CampsIcon />, hint: 'Communities on TrueMarch World' },
  { id: 'games', label: 'Games', href: '/games', icon: <GamesIcon />, hint: 'Play, and earn KHARMA' },
  { id: 'world', label: 'World', href: '/world', icon: <WorldIcon />, hint: 'TrueMarch 2D — walk the world of camps' },];

/**
 * The Games tab while a game runs (docs/GAMES_PAGE_REDESIGN.md §4): `tip` is its tooltip, `onClick` takes a
 * plain click (to the Games home). Drawn with the Gilded Arcade classes (components/games/arcade.css), so the
 * page that passes it must load that sheet and sit inside a `.ga-room`.
 */
export interface GamesTabOverride { tip: string; onClick: () => void }

/** A plain left click, which the page may take over; Ctrl, ⌘, Shift, Alt or a middle click stays the browser's (a new tab). */
const plainClick = (e: React.MouseEvent) => e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;

function Tabs({ here, gamesTab }: { here: Section | null; gamesTab?: GamesTabOverride }) {
  return (
    <nav aria-label="Sections" data-fit="tabs" className="flex h-full shrink-0 items-stretch gap-1">
      {SECTIONS.map((s) => {
        const on = s.id === here;
        if (s.id === 'games' && gamesTab) return (
          // The decorated tab: a gold-framed pill with a live pulse. `ga-tip` on the link, not the pill: the pill
          // clips its own sweep (overflow hidden), and both draw with ::after.
          <Link
            key={s.id}
            href={s.href}
            aria-current={on ? 'page' : undefined}
            aria-label={gamesTab.tip}
            data-tip={gamesTab.tip}
            onClick={(e) => { if (!plainClick(e)) return; e.preventDefault(); gamesTab.onClick(); }}
            className="ga-tip group relative flex shrink-0 items-center px-1 no-underline focus:outline-none focus-visible:[box-shadow:none] [&:focus-visible>span]:outline [&:focus-visible>span]:outline-2 [&:focus-visible>span]:outline-offset-2 [&:focus-visible>span]:outline-[#e9c46a]"
          >
            <span className="ga-live-tab transition group-hover:brightness-110">
              <span className="ga-pulse" aria-hidden />
              <GamesIcon className="h-4 w-4 shrink-0" />
              <span data-fit="labels">{s.label}</span>
            </span>
          </Link>
        );
        return (
          <Link
            key={s.id}
            href={s.href}
            title={s.hint}
            aria-current={on ? 'page' : undefined}
            className={
              'group relative flex shrink-0 items-center gap-2 px-2.5 text-[14px] no-underline transition-colors ' +
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-400 ' +
              (on
                ? 'font-semibold text-ink-900 dark:text-white'
                : 'font-medium text-ink-500 hover:text-ink-900 dark:text-ink-400 dark:hover:text-white')
            }
          >
            <span className={on ? 'text-accent-600 dark:text-accent-400' : ''}>{s.icon}</span>
            <span data-fit="labels">{s.label}</span>
            {/* The lit section: an accent rule on the bar's bottom edge. */}
            <span aria-hidden className={'absolute inset-x-2 bottom-0 h-[2px] rounded-full transition ' +
              (on ? 'bg-accent-600 dark:bg-accent-400' : 'bg-transparent group-hover:bg-ink-300 dark:group-hover:bg-white/25')} />
          </Link>
        );
      })}
    </nav>
  );
}

/** The tabs folded into one: the section you are in, and a list of all of them. */
function Switcher({ here, gamesTab }: { here: Section | null; gamesTab?: GamesTabOverride }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const pathname = usePathname();
  useEffect(() => { setOpen(false); }, [pathname]);
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [open]);
  const cur = SECTIONS.find((s) => s.id === here);
  return (
    <div ref={wrap} data-fit-alt="tabs" className="relative shrink-0">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}
              aria-label={`Sections${cur ? ` — ${cur.label}` : ''}`}
              className="flex h-9 items-center gap-1 rounded-full pl-2.5 pr-1.5 text-ink-700 transition hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-white/[0.08]">
        {cur?.icon ?? <TranslateIcon />}
        {gamesTab && here === 'games' && <span className="ga-pulse -ml-0.5 mb-3" aria-hidden />}
        <svg viewBox="0 0 24 24" className="h-4 w-4 text-ink-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><path d="M6 9l6 6 6-6" /></svg>
      </button>
      {open && (
        <div role="menu" className="absolute left-0 top-full z-[110] mt-2 w-60 rounded-2xl border border-ink-200 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-ink-900">
          {SECTIONS.map((s) => {
            // Folded, the Games tab keeps what it does while a game runs: its click, and its words as the hint.
            const live = s.id === 'games' && gamesTab;
            return (
              <Link key={s.id} role="menuitem" href={s.href} aria-current={s.id === here ? 'page' : undefined}
                    onClick={live ? (e) => { if (!plainClick(e)) return; e.preventDefault(); setOpen(false); live.onClick(); } : undefined}
                    className={'flex items-center gap-3 rounded-xl px-3 py-2.5 transition ' +
                      (s.id === here ? 'bg-ink-100 dark:bg-white/[0.08]' : 'hover:bg-ink-50 dark:hover:bg-white/[0.05]')}>
                <span className="text-ink-700 dark:text-ink-200">{s.icon}</span>
                <span className="min-w-0">
                  <span className="flex items-center gap-2 text-[14px] font-semibold text-ink-900 dark:text-white">
                    {s.label}{live && <span className="ga-pulse" aria-hidden />}
                  </span>
                  <span className={'block text-[12px] text-ink-500 dark:text-ink-400 ' + (live ? 'whitespace-normal' : 'truncate')}>{live ? live.tip : s.hint}</span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Sign in and sign up, for a signed-out browser: the bar's call to action. */
function JoinButtons() {
  return (
    <span className="flex shrink-0 items-center gap-1.5">
      <a href="/login" data-fit="signin"
         className="flex h-9 items-center rounded-full border border-ink-200 px-3.5 text-[13px] font-semibold text-ink-800 no-underline transition hover:bg-ink-50 dark:border-white/15 dark:text-ink-100 dark:hover:bg-white/[0.06]">
        Sign in
      </a>
      <a href="/register"
         className="flex h-9 items-center rounded-full bg-accent-600 px-3.5 text-[13px] font-semibold text-white no-underline transition hover:bg-accent-700">
        Sign up
      </a>
    </span>
  );
}

export function NavClose({ onClose, title = 'Close' }: { onClose: () => void; title?: string }) {
  return (
    <button type="button" onClick={onClose} title={title} aria-label={title}
            className={NAV_BTN + ' ring-1 ring-ink-200 dark:ring-white/15'}>
      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
        <path d="M6 6l12 12M18 6L6 18" />
      </svg>
    </button>
  );
}

const DROP = ['balance', 'wordmark', 'signin', 'labels', 'tabs'] as const;

/** The bar's height (h-14), which hiding it gives back to the page. */
const BAR_H = 56;

/**
 * Headroom: the bar steps out of the way while you read down and comes back
 * the moment you scroll up, as most professional sites do. It watches every
 * scroll on the page (capture phase), so it works whether the page scrolls the
 * window or its own pane (the Games shelf, the Camps feed). It never hides
 * near the top, while one of its menus is open, or when `off`.
 * `--nav-offset` on <html> tells sticky side columns where the bar ends.
 */
function useHeadroom(bar: React.RefObject<HTMLElement | null>, off: boolean) {
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    if (off) { setHidden(false); return; }
    const last = new WeakMap<EventTarget, number>();
    let travel = 0;
    let isHidden = false;
    /** Until when scroll events are only recorded, not acted on: the bar and the side columns are still moving. */
    let settleUntil = 0;
    const set = (h: boolean) => {
      if (h === isHidden) return;
      isHidden = h;
      travel = 0;
      settleUntil = performance.now() + 450;
      setHidden(h);
    };
    const onScroll = (e: Event) => {
      const t = e.target as (Element | Document);
      const el = t instanceof Document ? (t.scrollingElement ?? t.documentElement) : t;
      if (!(el instanceof Element) || bar.current?.contains(el)) return;
      // Only the page itself: a small scroller (a menu, a code box) is not it, and
      // neither are the side columns, which resize when the bar moves (scrolling
      // them would toggle the bar, which resizes them again: the flicker).
      if (el !== document.scrollingElement && (el.clientHeight < window.innerHeight * 0.5 || el.closest('aside'))) return;
      // An app pane that keeps the bar put (TrueMarch GPT's conversation: the layout is sized under the bar).
      if (el !== document.scrollingElement && el.closest('[data-nav-keep]')) return;
      const y = el.scrollTop;
      const prev = last.get(el) ?? y;
      last.set(el, y);
      if (y === prev) return;
      setScrolled(y > 4);
      if (y < BAR_H * 1.5) { set(false); return; }
      // While the bar slides, the page may shift under it: record, do not react.
      if (performance.now() < settleUntil) return;
      const d = y - prev;
      // At the very bottom the browser pulls the page back a little when its
      // height changes; that small "scroll up" is not the reader.
      const atBottom = y + el.clientHeight >= el.scrollHeight - 2;
      if (atBottom && d < 0 && d > -40) return;
      travel = Math.sign(d) === Math.sign(travel) ? travel + d : d;
      // A menu open in the bar (account, bell, sections) keeps it in place.
      // (The page's own `lead` button, the Camps ☰, toggles a side menu, not a dropdown.)
      if (bar.current?.querySelector('[aria-expanded="true"]:not([data-nav-lead] *)')) { set(false); return; }
      if (travel > 32) set(true);
      else if (travel < -24) set(false);
    };
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', onScroll, { capture: true });
  }, [off, bar]);
  useEffect(() => {
    document.documentElement.style.setProperty('--nav-offset', hidden ? '0px' : `${BAR_H}px`);
  }, [hidden]);
  useEffect(() => () => { document.documentElement.style.removeProperty('--nav-offset'); }, []);
  return { hidden, scrolled };
}

/**
 * The centre's width. The search is centred on the bar, so it can use what is
 * left once BOTH sides have room for the wider of the two (at most 560px).
 * Where that is too little (a phone), it takes whatever is free instead, at
 * least 64px (the glass and the start of the word); below that the row
 * overflows and lib/useFit drops things. (It was 120px, which on a 390px phone
 * with Sign up showing pushed the avatar 22px off the screen — after
 * everything droppable had already gone.)
 */
function useCentre(box: React.RefObject<HTMLElement | null>, left: React.RefObject<HTMLElement | null>, right: React.RefObject<HTMLElement | null>, on: boolean): number {
  const [w, setW] = useState(on ? 320 : 0);
  useEffect(() => {
    if (!on) { setW(0); return; }
    const b = box.current, l = left.current, r = right.current;
    if (!b || !l || !r) return;
    const fit = () => {
      const cs = getComputedStyle(b);
      const inner = b.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
      const gap = parseFloat(cs.columnGap) || 0;
      const lw = l.offsetWidth, rw = r.offsetWidth;
      const centred = inner - 2 * Math.max(lw, rw) - 2 * gap;
      const free = inner - lw - rw - 2 * gap;
      const next = Math.round(centred >= 280 ? Math.min(560, centred) : Math.max(64, Math.min(560, free)));
      setW((p) => (Math.abs(p - next) > 1 ? next : p));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(b); ro.observe(l); ro.observe(r);
    return () => ro.disconnect();
  }, [box, left, right, on]);
  return w;
}

export default function SiteNav({ lead, search, children, onClose, closeTitle, closeButton, gamesTab, sticky = true, tone = 'auto' }: {
  /** A page's own menu button, drawn before the mark (the Camps ☰). */
  lead?: ReactNode;
  /** The centre: this page's search. Omitted, a search of all of Kharma; `null`, none (a running game). */
  search?: ReactNode | null;
  /** This page's tools, on the right before KHARMA. */
  children?: ReactNode;
  /** Only for a level that is a layer over its page (a running game). */
  onClose?: () => void;
  closeTitle?: string;
  /** Drawn in place of the plain ✕ (a running game's own Close, components/games/GamePlayer.tsx). */
  closeButton?: ReactNode;
  /** The Games tab while a game runs: decorated, with its own tooltip and click (docs/GAMES_PAGE_REDESIGN.md §4). */
  gamesTab?: GamesTabOverride;
  /** Off where the bar is already the top of a full-window layer. */
  sticky?: boolean;
  /** `dark` over a page that is always dark (the Games shelf's night sky); otherwise the app's theme. */
  tone?: 'auto' | 'dark';
}) {
  const pathname = usePathname() || '/';
  const here = sectionOf(pathname);
  const { loaded, user, marq } = useAuth();
  const { ref } = useFit<HTMLDivElement>(DROP, `${pathname}|${loaded}|${user?.id ?? ''}|${marq?.balance ?? ''}`);
  const leftZone = useRef<HTMLDivElement>(null);
  const rightZone = useRef<HTMLDivElement>(null);
  const searchW = useCentre(ref, leftZone, rightZone, search !== null);
  const bar = useRef<HTMLElement>(null);
  // A running game (a layer with ✕) keeps its bar: its tools are the game's own.
  const { hidden, scrolled } = useHeadroom(bar, !!onClose || !!closeButton);

  return (
    <header
      ref={bar}
      data-hidden={hidden || undefined}
      style={sticky ? undefined : { marginTop: hidden ? -BAR_H - 1 : 0 }}
      className={
        (sticky ? 'sticky top-0 ' : 'relative ') +
        'z-[60] w-full shrink-0 border-b backdrop-blur-xl backdrop-saturate-150 ' +
        // Glass: the page shows through, blurred, so the bar belongs to it.
        // (`dark:` variants only reach INSIDE a `.dark` element, so the bar's own
        // dark tone is spelled out for tone="dark".)
        (tone === 'dark'
          ? 'bg-[#0b1020]/70 text-white '
          : 'bg-white/80 text-ink-900 supports-[backdrop-filter]:bg-white/70 dark:bg-ink-950/75 dark:text-ink-50 dark:supports-[backdrop-filter]:bg-ink-950/60 ') +
        'motion-safe:transition-[transform,margin,box-shadow,border-color,background-color] motion-safe:duration-300 motion-safe:ease-out ' +
        (sticky && hidden ? '-translate-y-full ' : 'translate-y-0 ') +
        (tone === 'dark'
          ? 'border-white/[0.08] shadow-[0_1px_16px_-4px_rgba(0,0,0,0.6)]'
          : scrolled || !sticky
            ? 'border-ink-200/80 shadow-[0_1px_12px_-4px_rgba(15,23,42,0.12)] dark:border-white/[0.08] dark:shadow-[0_1px_16px_-4px_rgba(0,0,0,0.6)]'
            : 'border-transparent')
      }
    >
      {/*
        A three-column grid: the side columns share what the centre leaves, so
        the search sits in the middle of the bar. They are `1fr` with an `auto`
        minimum, so they never squeeze their contents; when the row runs short,
        lib/useFit takes things away instead (DROP).
      */}
      <div ref={ref} className={(tone === 'dark' ? 'dark ' : '') + 'grid h-14 w-full grid-cols-[1fr_auto_1fr] items-center gap-2 px-3 sm:gap-4 sm:px-4'}>
        <div ref={leftZone} className="flex h-full items-center gap-2 justify-self-start sm:gap-3">
          {lead && <span data-nav-lead className="contents">{lead}</span>}
          <Link href="/" title={`${BRAND.name} — the translator`} aria-label={BRAND.name}
                className="flex shrink-0 items-center gap-2.5 rounded-full no-underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-400">
            <BrandMark className="h-8 w-8 shrink-0 object-contain drop-shadow-sm" />
            <span data-fit="wordmark" className="whitespace-nowrap text-[18px] font-bold leading-none tracking-[-0.02em] text-ink-900 dark:text-white">
              {BRAND.wordmark.lead} <span className="text-accent-600 dark:text-accent-400">{BRAND.wordmark.accent}</span>
            </span>
          </Link>
          <span className="mx-1 hidden h-5 w-px shrink-0 bg-ink-200 sm:block dark:bg-white/10" aria-hidden />
          {/* `contents`: once the tabs fold into the switcher, an empty box here would still cost the row a gap. */}
          <div className="contents">
            <Tabs here={here} gamesTab={gamesTab} />
          </div>
          <Switcher here={here} gamesTab={gamesTab} />
        </div>

        <div className="flex items-center justify-center" style={{ width: searchW }}>
          {search === undefined ? <CampSearch everything /> : search}
        </div>

        {/* `whitespace-nowrap`: "Sign up" must not count as two short lines when the centre measures what is free.
            The panels that open from here (rewards, notifications, the account menu) wrap normally again: they
            inherited the nowrap, so a long line pushed the rewards panel wider than itself (cut off, sideways scroll). */}
        <div ref={rightZone} className="flex items-center gap-1 justify-self-end whitespace-nowrap [&_[role=dialog]]:whitespace-normal [&_[role=menu]]:whitespace-normal">
          {children && <div className="mr-1 flex items-center gap-2">{children}</div>}
          <DailyReward />
          <ChatButton />
          {/* `\` opens the Dev Agent for developers (docs/DEV_AGENT.md); draws nothing otherwise. */}
          <DevAgentHotkey />
          <NotificationBell />
          {loaded && !user && <JoinButtons />}
          <AccountMenu />
          {closeButton ?? (onClose && <NavClose onClose={onClose} title={closeTitle} />)}
        </div>
      </div>
    </header>
  );
}
